import { create } from "zustand";
import { persist } from "zustand/middleware";

type VipRenewalIncentiveOfferKind = "first" | "recovery";
type VipRenewalIncentiveSurface = "modal" | "footerAd";

type VipRenewalIncentiveDismissedEndTimes = Record<
  VipRenewalIncentiveSurface,
  Record<VipRenewalIncentiveOfferKind, number>
>;

type VipRenewalIncentiveRuntimeState = {
  dismissedEndTimes: VipRenewalIncentiveDismissedEndTimes;
  setDismissedEndTime: (
    surface: VipRenewalIncentiveSurface,
    kind: VipRenewalIncentiveOfferKind,
    endTime: number,
  ) => void;
  clearDismissedEndTimes: (kind: VipRenewalIncentiveOfferKind) => void;
  resetVipRenewalIncentiveRuntime: () => void;
};

const defaultDismissedEndTimes = (): VipRenewalIncentiveDismissedEndTimes => ({
  modal: {
    first: 0,
    recovery: 0,
  },
  footerAd: {
    first: 0,
    recovery: 0,
  },
});

const normalizeEndTime = (endTime: number) => (
  Number.isFinite(endTime) ? Math.max(0, endTime) : 0
);

export const useVipRenewalIncentiveRuntimeStore = create<VipRenewalIncentiveRuntimeState>()(
  persist((set) => ({
    dismissedEndTimes: defaultDismissedEndTimes(),
    setDismissedEndTime: (surface, kind, endTime) => {
      set((state) => ({
        dismissedEndTimes: {
          ...state.dismissedEndTimes,
          [surface]: {
            ...state.dismissedEndTimes[surface],
            [kind]: normalizeEndTime(endTime),
          },
        },
      }));
    },
    clearDismissedEndTimes: (kind) => {
      set((state) => ({
        dismissedEndTimes: {
          modal: {
            ...state.dismissedEndTimes.modal,
            [kind]: 0,
          },
          footerAd: {
            ...state.dismissedEndTimes.footerAd,
            [kind]: 0,
          },
        },
      }));
    },
    resetVipRenewalIncentiveRuntime: () => {
      set({
        dismissedEndTimes: defaultDismissedEndTimes(),
      });
    },
  }), {
    name: "findin-vip-incentive-runtime",
    merge: (persistedState, currentState) => {
      const persisted = persistedState as Partial<VipRenewalIncentiveRuntimeState> | undefined;
      return {
        ...currentState,
        dismissedEndTimes: {
          modal: {
            ...currentState.dismissedEndTimes.modal,
            ...persisted?.dismissedEndTimes?.modal,
          },
          footerAd: {
            ...currentState.dismissedEndTimes.footerAd,
            ...persisted?.dismissedEndTimes?.footerAd,
          },
        },
      };
    },
  }),
);

export const useVipRenewalIncentiveRuntime = () => useVipRenewalIncentiveRuntimeStore((state) => ({
  dismissedEndTimes: state.dismissedEndTimes,
  setDismissedEndTime: state.setDismissedEndTime,
  clearDismissedEndTimes: state.clearDismissedEndTimes,
  resetVipRenewalIncentiveRuntime: state.resetVipRenewalIncentiveRuntime,
}));
